import React, { useEffect, useState, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { PayGatewayOptions, PayloftGatewayMessage } from "../types";
import { validateGatewayOptions, isTrustedMessage } from "../utils/security";

export interface PayModalProps {
  isOpen: boolean;
  onCloseModal: () => void;
  options: PayGatewayOptions;
}

const DEFAULT_GATEWAY_URL = "https://checkout.payloft.com";

export const PayModal: React.FC<PayModalProps> = ({
  isOpen,
  onCloseModal,
  options,
}) => {
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [redirectText, setRedirectText] = useState("Processing...");
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isOpen) {
      validateGatewayOptions(options);
    }
  }, [isOpen, options]);

  const {
    reference,
    publicKey,
    gatewayUrl = DEFAULT_GATEWAY_URL,
    onSuccess,
    onClose,
    onError,
    onReady,
    customStyles,
    hideCloseButton = true,
    closeOnBackdropClick = true,
    timeoutSeconds = 30,
    metadata,
    redirectDelay = 5000,
    isSandbox = false,
  } = options;

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Compute base URL and Origin for strict postMessage validation
  const cleanGatewayUrl = (gatewayUrl || DEFAULT_GATEWAY_URL).replace(/\/+$/, "");
  let gatewayOrigin = "*";
  try {
    gatewayOrigin = new URL(cleanGatewayUrl).origin;
  } catch (e) {
    gatewayOrigin = "*";
  }

  // Construct iframe source URL
  const parentOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const queryParams = new URLSearchParams();
  queryParams.set("ref", reference);
  queryParams.set("parentOrigin", parentOrigin);
  queryParams.set("embed", "true");
  if (publicKey) queryParams.set("publicKey", publicKey);

  if (metadata) {
    Object.entries(metadata).forEach(([key, val]) => {
      queryParams.set(`meta_${key}`, String(val));
    });
  }

  const basePath = isSandbox && !cleanGatewayUrl.includes("/sandbox-pay")
    ? `${cleanGatewayUrl}/sandbox-pay/card`
    : cleanGatewayUrl;

  const iframeSrc = `${basePath}/${encodeURIComponent(reference)}?${queryParams.toString()}`;

  // Handle Close
  const handleClose = useCallback(() => {
    onCloseModal();
    if (onClose) {
      onClose();
    }
  }, [onCloseModal, onClose]);

  // Handle Message Events from Gateway IFrame
  useEffect(() => {
    if (!isOpen) return;

    const handleMessage = (event: MessageEvent) => {
      // Strict Security Verification
      if (!isTrustedMessage(event, iframeRef.current?.contentWindow || null, gatewayOrigin)) {
        return;
      }

      const data = event.data as PayloftGatewayMessage;
      if (!data || typeof data !== "object") return;

      switch (data.type) {
        case "pay:ready":
        case "PAYLOFT_EMBED_READY":
          setLoading(false);
          if (onReady) onReady();
          break;

        case "pay:success":
        case "PAYLOFT_TRANSACTION_SUCCESS":
          setLoading(false);
          if (data.payload?.reference && data.payload.reference !== reference) {
            console.warn(
              `[Payloft Security] Warning: Response reference mismatch (expected ${reference}, received ${data.payload.reference})`
            );
          }
          if (onSuccess) {
            onSuccess(data.payload || { reference, status: "success" });
          }
          // Do not close immediately; wait for redirect to callback URL
          if (!fallbackTimerRef.current) {
            fallbackTimerRef.current = setTimeout(() => {
              handleClose();
            }, 15000);
          }
          break;

        case "pay:redirecting":
        case "PAYLOFT_TRANSACTION_REDIRECTING":
          setLoading(false);
          setIsRedirecting(true);
          setRedirectText(
            data.payload?.status === "expired"
              ? "Transaction Expired..."
              : "Processing..."
          );
          if (fallbackTimerRef.current) {
            clearTimeout(fallbackTimerRef.current);
            fallbackTimerRef.current = null;
          }
          if (!redirectTimerRef.current) {
            redirectTimerRef.current = setTimeout(() => {
              handleClose();
            }, redirectDelay);
          }
          break;

        case "pay:close":
        case "PAYLOFT_TRANSACTION_CANCELLED":
          handleClose();
          break;

        case "pay:error":
        case "PAYLOFT_TRANSACTION_FAILED":
          setLoading(false);
          if (onError) {
            onError(
              data.payload || {
                reference,
                message: "Transaction failed or error occurred",
              }
            );
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener("message", handleMessage);

    // Timeout fallback if iframe doesn't finish loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, timeoutSeconds * 1000);

    return () => {
      window.removeEventListener("message", handleMessage);
      clearTimeout(timer);
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = null;
      }
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };
  }, [isOpen, gatewayOrigin, reference, onSuccess, onError, onReady, handleClose, timeoutSeconds, redirectDelay]);

  // Handle ESC key to dismiss
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && closeOnBackdropClick) {
        handleClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeOnBackdropClick, handleClose]);

  // Lock background scroll when modal is active
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  if (!mounted || !isOpen) return null;

  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(15, 23, 42, 0.65)",
    backdropFilter: "blur(4px)",
    WebkitBackdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999999,
    padding: "16px",
    boxSizing: "border-box",
    animation: "payloft-fade-in 0.2s ease-out forwards",
    ...customStyles?.overlay,
  };

  const modalContainerStyle: React.CSSProperties = {
    position: "relative",
    width: "100%",
    maxWidth: "780px",
    height: "650px",
    maxHeight: "95vh",
    backgroundColor: "#ffffff",
    borderRadius: "0px",
    border: "1px solid #cbd5e1",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.35)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    animation: "payloft-scale-in 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards",
    ...customStyles?.modal,
  };

  const iframeStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    border: "none",
    borderRadius: "0px",
    backgroundColor: "#ffffff",
    ...customStyles?.iframe,
  };

  const closeBtnStyle: React.CSSProperties = {
    position: "absolute",
    top: "12px",
    right: "12px",
    zIndex: 10,
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    backgroundColor: "rgba(241, 245, 249, 0.9)",
    border: "1px solid rgba(203, 213, 225, 0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#475569",
    fontSize: "18px",
    fontWeight: "bold",
    transition: "all 0.15s ease",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    padding: 0,
    lineHeight: 1,
    ...customStyles?.closeButton,
  };

  const loaderContainerStyle: React.CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#ffffff",
    zIndex: 5,
    gap: "12px",
    ...customStyles?.loader,
  };

  return ReactDOM.createPortal(
    <div
      style={overlayStyle}
      onClick={(e) => {
        if (e.target === e.currentTarget && closeOnBackdropClick) {
          handleClose();
        }
      }}
      role="dialog"
      aria-modal="true"
    >
      <style>{`
        @keyframes payloft-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes payloft-scale-in {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes payloft-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div style={modalContainerStyle}>
        {loading && (
          <div style={loaderContainerStyle}>
            <div
              style={{
                width: "36px",
                height: "36px",
                border: "3px solid #e2e8f0",
                borderTopColor: "#059669",
                borderRadius: "50%",
                animation: "payloft-spin 0.8s linear infinite",
              }}
            />
            <span
              style={{
                fontSize: "14px",
                color: "#64748b",
                fontFamily: "system-ui, -apple-system, sans-serif",
              }}
            >
              Loading Payloft Checkout...
            </span>
          </div>
        )}

        <iframe
          ref={iframeRef}
          src={iframeSrc}
          title="Payloft Checkout"
          style={iframeStyle}
          allow="camera; payment; geolocation"
          onLoad={() => setLoading(false)}
        />

        {isRedirecting && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "#ffffff",
              zIndex: 50,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                width: "42px",
                height: "42px",
                border: "3px solid #e2e8f0",
                borderTopColor: "#00aeef",
                borderRadius: "50%",
                animation: "payloft-spin 0.8s linear infinite",
                marginBottom: "16px",
              }}
            />
            <h3
              style={{
                margin: "0 0 8px 0",
                fontSize: "18px",
                fontWeight: 600,
                color: "#0f172a",
                fontFamily: "system-ui, -apple-system, sans-serif",
                textAlign: "center",
              }}
            >
              {redirectText}
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#64748b",
                fontFamily: "system-ui, -apple-system, sans-serif",
                textAlign: "center",
              }}
            >
              Finalizing transaction, closing in a moment...
            </p>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
